import { useEffect, useState } from "react";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Copy,
  Eye,
  FileText,
  FileUp,
  Info,
  Loader2,
  Plus,
  Sparkles,
  Upload,
  Wrench,
  X,
} from "lucide-react";
import { api, ApiError } from "../../lib/api";
import type { AdminResource } from "../../lib/api";
import { Button } from "../ui/Button";
import { ShortNotesAdminPreviewModal } from "./ShortNotesAdminPreviewModal";
import { QuestionFigure } from "../QuestionFigure";
import { QuestionFigureUploader } from "./QuestionFigureUploader";
import { Modal } from "../ui/Modal";

// ── Types ────────────────────────────────────────────────────────────────────

type ManualQuizQ = {
  question: string;
  options: string[];
  correct: number;
  difficulty: "Easy" | "Medium" | "Hard";
  explanation: string;
  chapterTitle: string;
  topicTitle: string;
  sourceReference: string;
  figure_status?: "none" | "required" | "uploaded";
  figure_image?: string | null;
  figure_alt?: string | null;
  figure_location?: any;
};

type ManualExamQ = {
  number: number;
  text: string;
  question_type?: string;
  section_id?: string;
  section?: string;
  instruction?: string;
  source_id?: string;
  options: { label: string; text: string }[];
  answer: { label: string; text?: string; explanation: string; confidence?: string };
  figure_status?: "none" | "required" | "uploaded";
  figure_image?: string | null;
  figure_alt?: string | null;
  figure_location?: any;
};

type ContentMode = "unified" | "quiz" | "exam";

function emptyQuizQ(): ManualQuizQ {
  return {
    question: "",
    options: ["", "", "", ""],
    correct: 0,
    difficulty: "Medium",
    explanation: "",
    chapterTitle: "",
    topicTitle: "",
    sourceReference: "",
  };
}

function emptyExamQ(num: number): ManualExamQ {
  return {
    number: num,
    text: "",
    options: [
      { label: "A", text: "" },
      { label: "B", text: "" },
      { label: "C", text: "" },
      { label: "D", text: "" },
    ],
    answer: { label: "A", explanation: "" },
  };
}

function parseQuestionFigureData(q: Record<string, unknown>, qIndex: number): {
  figure_status: "none" | "required" | "uploaded";
  figure_image: string | null;
  figure_alt: string | null;
  figure_location?: any;
} | string {
  let figStatus: "none" | "required" | "uploaded" = "none";
  let figImage: string | null = (q.figure_image as string) || (q.figureImage as string) || null;
  let figAlt: string | null = (q.figure_alt as string) || (q.figureAlt as string) || null;
  let figLoc: any = q.figure_location || q.figureLocation || null;

  if (q.figure && typeof q.figure === "object") {
    const f = q.figure as Record<string, unknown>;
    if (f.required === true || f.required === "true") {
      figStatus = figImage ? "uploaded" : "required";
    }
    if (f.alt) figAlt = String(f.alt).trim();
    if (f.location && typeof f.location === "object") {
      const loc = f.location as Record<string, unknown>;
      if (loc.pdfPage != null) {
        const pageNum = Number(loc.pdfPage);
        if (!Number.isInteger(pageNum) || pageNum <= 0) {
          return `Q${qIndex + 1}: figure.location.pdfPage must be a positive integer.`;
        }
      }
      figLoc = loc;
    }
  }

  const directStatus = q.figure_status || q.figureStatus;
  if (directStatus === "required" || directStatus === "uploaded" || directStatus === "none") {
    figStatus = directStatus as "none" | "required" | "uploaded";
  }
  if (figImage && figStatus !== "none") {
    figStatus = "uploaded";
  }

  return {
    figure_status: figStatus,
    figure_image: figImage,
    figure_alt: figAlt,
    figure_location: figLoc,
  };
}

function parseJsonQuestions(
  raw: string,
  mode: ContentMode,
): ManualQuizQ[] | ManualExamQ[] | string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return "Invalid JSON — check brackets and quotes.";
  }
  const arr = Array.isArray(parsed)
    ? parsed
    : (parsed as { questions?: unknown })?.questions;
  if (!Array.isArray(arr)) return "JSON must be an array or { questions: [...] }.";
  if (arr.length === 0) return "Array is empty.";

  if (mode === "quiz") {
    const out: ManualQuizQ[] = [];
    for (let i = 0; i < arr.length; i++) {
      const q = arr[i] as Record<string, unknown>;
      if (!q.question || !Array.isArray(q.options) || q.correct == null) {
        return `Q${i + 1}: needs "question", "options" array, and "correct" index.`;
      }
      const opts = (q.options as unknown[]).map((o) => String(o ?? "").trim());
      const correct = Number(q.correct);
      if (correct < 0 || correct >= opts.length) {
        return `Q${i + 1}: "correct" ${correct} out of range for ${opts.length} options.`;
      }
      const figRes = parseQuestionFigureData(q, i);
      if (typeof figRes === "string") return figRes;

      out.push({
        question: String(q.question).trim(),
        options: opts.slice(0, 4),
        correct,
        difficulty: ["Easy", "Medium", "Hard"].includes(String(q.difficulty))
          ? (String(q.difficulty) as "Easy" | "Medium" | "Hard")
          : "Medium",
        explanation: String(q.explanation ?? "").trim(),
        chapterTitle: String(q.chapterTitle ?? "").trim(),
        topicTitle: String(q.topicTitle ?? "").trim(),
        sourceReference: String(q.sourceReference ?? "").trim(),
        ...figRes,
      });
    }
    return out;
  } else {
    const out: ManualExamQ[] = [];
    for (let i = 0; i < arr.length; i++) {
      const q = arr[i] as Record<string, unknown>;
      const ans = q.answer as Record<string, unknown> | undefined;
      if (!q.text || !Array.isArray(q.options)) {
        return `Q${i + 1}: needs "text" and "options" array.`;
      }
      const opts = (q.options as unknown[]).map((o) => {
        const obj = o as Record<string, unknown>;
        return {
          label: String(obj.label ?? "").toUpperCase().trim(),
          text: String(obj.text ?? "").trim(),
        };
      });
      const ansLabel = ans?.label != null ? String(ans.label).toUpperCase().trim() : "";
      if (opts.length > 0 && ansLabel && !opts.some((o) => o.label === ansLabel)) {
        return `Q${i + 1}: answer label "${ansLabel}" not found in options.`;
      }
      const figRes = parseQuestionFigureData(q, i);
      if (typeof figRes === "string") return figRes;

      out.push({
        number: Number(q.number ?? i + 1),
        text: String(q.text).trim(),
        question_type: String(q.question_type ?? "multiple_choice").trim(),
        section_id: q.section_id ? String(q.section_id).trim() : undefined,
        section: q.section ? String(q.section).trim() : undefined,
        instruction: q.instruction ? String(q.instruction).trim() : undefined,
        source_id: q.source_id ? String(q.source_id).trim() : undefined,
        options: opts,
        answer: {
          label: ansLabel,
          text: String(ans?.text ?? "").trim(),
          explanation: String(ans?.explanation ?? "").trim(),
          confidence: String(ans?.confidence ?? "high").trim(),
        },
        ...figRes,
      });
    }
    return out;
  }
}

// ── Component ────────────────────────────────────────────────────────────────

export function AdminManualContentModal({
  open,
  token,
  onClose,
  onDone,
  courses,
  existingResources,
}: {
  open: boolean;
  token: string | null;
  onClose: () => void;
  onDone: () => void;
  courses: { id: string; name: string; code: string; subject?: string }[];
  existingResources: AdminResource[];
}) {
  const [mode, setMode] = useState<ContentMode>("unified");
  const [inputMode, setInputMode] = useState<"json" | "manual">("json");
  const [courseId, setCourseId] = useState("");
  const [moduleId, setModuleId] = useState("");
  const [subject, setSubject] = useState("");
  const [difficulty, setDifficulty] = useState<"Easy" | "Medium" | "Hard">("Medium");
  const [examClass, setExamClass] = useState<"" | "final" | "mid" | "coc">("");
  const [examTitle, setExamTitle] = useState("");
  const [examYear, setExamYear] = useState("");
  const [replaceExisting, setReplaceExisting] = useState(false);

  const [universities, setUniversities] = useState<{ id: string; name: string }[]>([]);
  const [universityId, setUniversityId] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<Array<{ name: string; size: number; content: string }>>([]);

  // Unified Import state
  const [unifiedStatus, setUnifiedStatus] = useState<"draft" | "active">("draft");
  const [unifiedReplaceExisting, setUnifiedReplaceExisting] = useState(false);
  const [unifiedDryRunning, setUnifiedDryRunning] = useState(false);
  const [unifiedSubmitting, setUnifiedSubmitting] = useState(false);
  const [repairingLatex, setRepairingLatex] = useState(false);
  const [unifiedResult, setUnifiedResult] = useState<any | null>(null);
  const [latexRepairNotice, setLatexRepairNotice] = useState<{
    wasRepaired: boolean;
    changeCount: number;
    changes: Array<{ line: number; col: number; original: string; repaired: string }>;
  } | null>(null);
  const [showShortNotesPreview, setShowShortNotesPreview] = useState(false);

  const [modules, setModules] = useState<{ id: string; title: string }[]>([]);
  const [modulesLoading, setModulesLoading] = useState(false);

  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState("");

  const [quizQuestions, setQuizQuestions] = useState<ManualQuizQ[]>([emptyQuizQ()]);
  const [examQuestions, setExamQuestions] = useState<ManualExamQ[]>([emptyExamQ(1)]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [quizStatus, setQuizStatus] = useState<"draft" | "active">("active");

  useEffect(() => {
    if (!open) return;
    api.universities().then((res) => {
      if (Array.isArray(res)) setUniversities(res);
    }).catch(() => {});
    setMode("unified");
    setInputMode("json");
    setUniversityId("");
    setCourseId("");
    setModuleId("");
    setSelectedFiles([]);
    setSubject("");
    setDifficulty("Medium");
    setExamClass("");
    setExamTitle("");
    setExamYear("");
    setReplaceExisting(false);
    setUnifiedStatus("draft");
    setUnifiedReplaceExisting(false);
    setUnifiedResult(null);
    setLatexRepairNotice(null);
    setJsonText("");
    setJsonError("");
    setQuizQuestions([emptyQuizQ()]);
    setExamQuestions([emptyExamQ(1)]);
    setQuizStatus("active");
    setSubmitError("");
    setSubmitSuccess("");
  }, [open]);

  useEffect(() => {
    if (!courseId) {
      setModules([]);
      return;
    }
    const mods = existingResources
      .filter((r) => r.courseId === courseId && r.resourceType === "module")
      .map((r) => ({ id: r.id, title: r.title }));
    setModules(mods);
  }, [courseId, existingResources]);

  function handleCourseChange(cid: string) {
    setCourseId(cid);
    setModuleId("");
    const selected = courses.find((c) => c.id === cid);
    if (selected) {
      setSubject(selected.subject || selected.name);
    }
  }

  function resetState() {
    setMode("unified");
    setInputMode("json");
    setUniversityId("");
    setCourseId("");
    setModuleId("");
    setSelectedFiles([]);
    setModules([]);
    setSubject("");
    setDifficulty("Medium");
    setExamClass("");
    setExamTitle("");
    setExamYear("");
    setReplaceExisting(false);
    setUnifiedStatus("draft");
    setUnifiedReplaceExisting(false);
    setUnifiedResult(null);
    setLatexRepairNotice(null);
    setQuizQuestions([emptyQuizQ()]);
    setExamQuestions([emptyExamQ(1)]);
    setQuizStatus("active");
    setJsonText("");
    setJsonError("");
    setSubmitError("");
    setSubmitSuccess("");
  }

  function handleClose() {
    resetState();
    onClose();
  }

  // ── Unified Templates ────────────────────────────────────────────────────────
  const CONTENT_TEMPLATE = JSON.stringify(
    {
      kind: "content",
      courseId: "",
      moduleId: "",
      chapterId: "",
      chapterTitle: "Chapter 1: Kinematics",
      replaceExisting: false,
      status: "draft",
      topics: [
        {
          topicId: null,
          topicTitle: "1.1 Motion in One Dimension",
          markdown: "Displacement along a straight line: $\\Delta x = x_2 - x_1$. Velocity: $v = \\frac{dx}{dt}$.",
          pages: [1, 10],
        },
      ],
    },
    null,
    2,
  );

  const NOTES_TEMPLATE = JSON.stringify(
    {
      kind: "notes",
      courseId: "",
      moduleId: "",
      chapterId: "",
      chapterTitle: "Chapter 1: Kinematics",
      replaceExisting: false,
      status: "draft",
      notes: [
        {
          topicId: null,
          topicTitle: "1.1 Motion in One Dimension",
          markdown: "### Key Principles\n- Acceleration: $a = \\frac{dv}{dt}$\n- Constant acceleration: $v = v_0 + at$",
          keyTerms: [
            { term: "Displacement", definition: "Change in position along a straight line." },
            { term: "Velocity", definition: "Rate of change of position with respect to time." },
          ],
          sourceReference: "Chapter 1, pp. 1-10",
        },
      ],
    },
    null,
    2,
  );

  const UNIFIED_QUIZ_TEMPLATE = JSON.stringify(
    {
      kind: "quiz",
      courseId: "",
      moduleId: "",
      chapterId: "",
      chapterTitle: "Chapter 1: Kinematics",
      subject: "Physics",
      difficulty: "Medium",
      status: "draft",
      batchId: "batch-kinematics-01",
      replaceExisting: false,
      questions: [
        {
          question: "What is the SI unit of acceleration?",
          options: ["m/s²", "m/s", "kg·m/s", "N/m"],
          correct: 0,
          difficulty: "Easy",
          explanation: "The standard SI unit for acceleration is meters per second squared (m/s²).",
          chapterTitle: "Chapter 1: Kinematics",
          topicTitle: "1.1 Motion in One Dimension",
          sourceReference: "p. 5",
          type: "multiple_choice",
        },
      ],
    },
    null,
    2,
  );

  // ── Templates ──────────────────────────────────────────────────────────────
  const QUIZ_TEMPLATE = JSON.stringify(
    [
      {
        question: "What is Newton's second law?",
        options: ["F = ma", "E = mc²", "v = u + at", "P = mv"],
        correct: 0,
        difficulty: "Medium",
        explanation: "Force equals mass times acceleration.",
        chapterTitle: "Chapter 2 – Dynamics",
        topicTitle: "Newton's Laws",
        sourceReference: "p.45",
      },
      {
        question: "What is the unit of electric charge?",
        options: ["Volt", "Ampere", "Coulomb", "Joule"],
        correct: 2,
        difficulty: "Easy",
        explanation: "The SI unit of electric charge is the Coulomb.",
        chapterTitle: "",
        topicTitle: "",
        sourceReference: "",
      },
    ],
    null,
    2,
  );

  const EXAM_TEMPLATE = JSON.stringify(
    [
      {
        number: 1,
        text: "What is the speed of light in a vacuum?",
        options: [
          { label: "A", text: "3 × 10⁸ m/s" },
          { label: "B", text: "3 × 10⁶ m/s" },
          { label: "C", text: "3 × 10¹⁰ m/s" },
          { label: "D", text: "1.5 × 10⁸ m/s" },
        ],
        answer: {
          label: "A",
          explanation: "The speed of light in vacuum is approximately 3 × 10⁸ m/s.",
        },
      },
      {
        number: 2,
        text: "Which gas is most abundant in Earth's atmosphere?",
        options: [
          { label: "A", text: "Oxygen" },
          { label: "B", text: "Carbon dioxide" },
          { label: "C", text: "Nitrogen" },
          { label: "D", text: "Argon" },
        ],
        answer: { label: "C", explanation: "Nitrogen makes up about 78% of Earth's atmosphere." },
      },
    ],
    null,
    2,
  );

  const [templateLoaded, setTemplateLoaded] = useState(false);

  function loadTemplate() {
    setJsonText(mode === "quiz" ? QUIZ_TEMPLATE : EXAM_TEMPLATE);
    setJsonError("");
    setTemplateLoaded(true);
    setTimeout(() => setTemplateLoaded(false), 2000);
  }

  function validateJson(): ManualQuizQ[] | ManualExamQ[] | null {
    if (!jsonText.trim()) {
      setJsonError("Paste JSON questions first.");
      return null;
    }
    const result = parseJsonQuestions(jsonText, mode);
    if (typeof result === "string") {
      setJsonError(result);
      return null;
    }
    setJsonError("");
    if (mode === "exam") {
      try {
        const parsedObj = JSON.parse(jsonText);
        if (parsedObj && typeof parsedObj === "object" && !Array.isArray(parsedObj)) {
          if (parsedObj.metadata?.title && !examTitle.trim()) {
            setExamTitle(String(parsedObj.metadata.title));
          }
          if (parsedObj.metadata?.year && !examYear) {
            setExamYear(String(parsedObj.metadata.year));
          }
        }
      } catch {
        // ignore
      }
    }
    return result as ManualQuizQ[] | ManualExamQ[];
  }

  function countJsonItems(): number | "?" {
    if (!jsonText.trim()) return "?" as unknown as number;
    try {
      const p = JSON.parse(jsonText);
      return Array.isArray(p) ? p.length : (p?.questions?.length ?? ("?" as unknown as number));
    } catch {
      return "?" as unknown as number;
    }
  }

  function detectPayloadKind(text: string): "content" | "notes" | "quiz" | null {
    if (!text.trim()) return null;
    try {
      const p = JSON.parse(text);
      if (Array.isArray(p)) return "quiz";
      if (p?.kind === "content" || Array.isArray(p?.topics)) return "content";
      if (p?.kind === "notes" || Array.isArray(p?.notes)) return "notes";
      if (p?.kind === "quiz" || Array.isArray(p?.questions)) return "quiz";
    } catch {
      if (text.includes('"topics"')) return "content";
      if (text.includes('"notes"')) return "notes";
      if (text.includes('"questions"')) return "quiz";
    }
    return null;
  }

  function checkUnescapedLatex(text: string): boolean {
    if (!text.trim()) return false;
    try {
      JSON.parse(text);
      return false;
    } catch {
      return /\\[a-zA-Z]+/.test(text);
    }
  }

  async function handleRepairLatex() {
    if (!token || !jsonText.trim()) return;
    setRepairingLatex(true);
    setSubmitError("");
    try {
      const res = await api.admin.repairLatex(token, jsonText);
      if (res.wasRepaired) {
        setJsonText(res.repairedJson);
        setLatexRepairNotice(res);
        setSubmitSuccess(`✓ Auto-repaired ${res.changeCount} unescaped LaTeX backslash token(s).`);
      } else {
        setSubmitError("No unescaped LaTeX backslashes could be automatically repaired.");
      }
    } catch (err: any) {
      setSubmitError(err?.message || "Failed to repair LaTeX backslashes.");
    } finally {
      setRepairingLatex(false);
    }
  }

  async function handleUnifiedSubmit(isDryRun: boolean) {
    if (!token) return;
    if (!courseId) {
      setSubmitError("Please select a Course before proceeding.");
      return;
    }
    if (!moduleId) {
      setSubmitError("Please select a Module before proceeding.");
      return;
    }
    const hasFiles = selectedFiles.length > 0;
    const hasText = Boolean(jsonText.trim());
    if (!hasFiles && !hasText) {
      setSubmitError("Please select files or paste JSON first.");
      return;
    }

    setSubmitError("");
    setSubmitSuccess("");
    if (isDryRun) setUnifiedDryRunning(true);
    else setUnifiedSubmitting(true);

    try {
      let res;
      if (hasFiles) {
        res = await api.admin.unifiedImport(token, {
          files: selectedFiles.map((f) => ({
            filename: f.name,
            rawJson: f.content,
            size: f.size,
          })),
          courseId,
          moduleId,
          universityId: universityId || undefined,
          dryRun: isDryRun,
          status: unifiedStatus,
          replaceExisting: unifiedReplaceExisting,
        });
      } else {
        res = await api.admin.unifiedImport(token, {
          rawJson: jsonText,
          courseId,
          moduleId,
          universityId: universityId || undefined,
          dryRun: isDryRun,
          status: unifiedStatus,
          replaceExisting: unifiedReplaceExisting,
        });
      }
      setUnifiedResult(res);

      if (res.wasRepaired && res.repairChanges?.length) {
        setLatexRepairNotice({
          wasRepaired: true,
          changeCount: res.repairChanges.length,
          changes: res.repairChanges,
        });
      }

      if (res.valid) {
        if (isDryRun) {
          setSubmitSuccess("✓ Dry run passed! Review counts and validation details below.");
        } else {
          setSubmitSuccess(`✓ Successfully imported! Status stays "${res.status || unifiedStatus}" until published.`);
          onDone();
        }
      } else {
        const firstErr =
          res.errors?.[0]?.message ||
          (res.results?.find((r: any) => !r.valid)?.errors?.[0]?.message) ||
          "Validation failed. Review errors below.";
        setSubmitError(firstErr);
      }
    } catch (err: any) {
      const msg = err instanceof ApiError ? err.message : String(err?.message || err);
      setSubmitError(msg);
    } finally {
      setUnifiedDryRunning(false);
      setUnifiedSubmitting(false);
    }
  }

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    const filesArray = Array.from(fileList);
    const loaded: Array<{ name: string; size: number; content: string }> = [];
    let completed = 0;

    filesArray.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const text = (evt.target?.result as string) || "";
        loaded.push({ name: file.name, size: file.size, content: text });
        completed++;
        if (completed === filesArray.length) {
          setSelectedFiles(loaded);
          setUnifiedResult(null);
          setLatexRepairNotice(null);
          setSubmitError("");
          setSubmitSuccess(`Loaded ${filesArray.length} file(s).`);
          if (filesArray.length === 1 && loaded[0]) {
            setJsonText(loaded[0].content);
          }
        }
      };
      reader.readAsText(file);
    });
    e.target.value = "";
  }

  function handleRemoveFile(index: number) {
    setSelectedFiles((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0) {
        setJsonText("");
      } else if (updated.length === 1) {
        setJsonText(updated[0].content);
      }
      return updated;
    });
    setUnifiedResult(null);
    setSubmitSuccess("");
    setSubmitError("");
  }

  async function handleSubmit() {
    if (mode === "unified") {
      await handleUnifiedSubmit(false);
      return;
    }
    if (!token) return;
    setSubmitError("");
    setSubmitSuccess("");

    if (!courseId) { setSubmitError("Select a course."); return; }
    if (mode === "quiz" && !subject.trim()) { setSubmitError("Subject is required."); return; }
    if (mode === "exam" && !examTitle.trim()) { setSubmitError("Exam Name / Title is required."); return; }

    let finalQuizQs: ManualQuizQ[] = quizQuestions;
    let finalExamQs: ManualExamQ[] = examQuestions;

    if (inputMode === "json") {
      const parsed = validateJson();
      if (!parsed) {
        setSubmitError(jsonError || "Invalid JSON format. Check error message above input.");
        return;
      }
      if (mode === "quiz") finalQuizQs = parsed as ManualQuizQ[];
      else finalExamQs = parsed as ManualExamQ[];
    } else {
      if (mode === "quiz") {
        for (let i = 0; i < finalQuizQs.length; i++) {
          const q = finalQuizQs[i];
          if (!q.question.trim()) { setSubmitError(`Q${i + 1}: question text is required.`); return; }
          if (q.options.filter((o) => o.trim()).length < 2) {
            setSubmitError(`Q${i + 1}: at least 2 non-empty options required.`); return;
          }
        }
      } else {
        for (let i = 0; i < finalExamQs.length; i++) {
          const q = finalExamQs[i];
          if (!q.text.trim()) { setSubmitError(`Q${i + 1}: question text is required.`); return; }
          if (q.options.filter((o) => o.text.trim()).length < 2) {
            setSubmitError(`Q${i + 1}: at least 2 non-empty options required.`); return;
          }
          if (!q.options.some((o) => o.label === q.answer.label && o.text.trim())) {
            setSubmitError(`Q${i + 1}: answer label not found in options.`); return;
          }
        }
      }
    }

    const missingQuizFigs = finalQuizQs.filter((q) => q.figure_status === "required" && !q.figure_image);
    const missingExamFigs = finalExamQs.filter((q) => q.figure_status === "required" && !q.figure_image);
    if (mode === "quiz" && missingQuizFigs.length > 0) {
      setSubmitError(`Cannot inject quiz: ${missingQuizFigs.length} question(s) need an image.`);
      return;
    }
    if (mode === "exam" && missingExamFigs.length > 0) {
      setSubmitError(`Cannot inject exam: ${missingExamFigs.length} question(s) need an image.`);
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "quiz") {
        const res = await api.admin.injectQuiz(token, {
          subject: subject.trim(),
          difficulty,
          examClass: examClass || null,
          courseId: courseId || null,
          moduleId: moduleId || null,
          status: quizStatus,
          replaceExisting,
          questions: finalQuizQs.map((q) => ({
            ...q,
            options: q.options.filter((o) => o.trim()),
          })),
        });
        setSubmitSuccess(
          quizStatus === "active"
            ? `✓ ${res.questionCount} question${res.questionCount === 1 ? "" : "s"} published in quiz cache for subject "${res.subject}". Students can practice them immediately.`
            : `✓ ${res.questionCount} question${res.questionCount === 1 ? "" : "s"} stored in draft. You can review, approve, and publish them in the Review Queue.`,
        );
      } else {
        let topMeta: Record<string, unknown> | undefined;
        let topSections: Array<Record<string, unknown>> | undefined;
        let topSources: Array<Record<string, unknown>> | undefined;

        if (inputMode === "json" && jsonText.trim()) {
          try {
            const parsedObj = JSON.parse(jsonText);
            if (parsedObj && typeof parsedObj === "object" && !Array.isArray(parsedObj)) {
              if (parsedObj.metadata) topMeta = parsedObj.metadata;
              if (Array.isArray(parsedObj.sections)) topSections = parsedObj.sections;
              if (Array.isArray(parsedObj.sources)) topSources = parsedObj.sources;
            }
          } catch {
            // Handled in validateJson
          }
        }

        const res = await api.admin.injectExamManual(token, {
          title: examTitle.trim(),
          courseId,
          course_id: courseId,
          examClass: examClass || null,
          exam_class: examClass || null,
          year: examYear ? Number(examYear) : null,
          replace: replaceExisting,
          replaceExisting,
          metadata: topMeta,
          sections: topSections,
          sources: topSources,
          questions: finalExamQs.map((q) => ({
            ...q,
            options: q.options.filter((o) => o.text.trim()),
          })),
        });
        setSubmitSuccess(
          `✓ Past exam "${res.title}" created/updated with ${res.newQuestionsAdded} new question${res.newQuestionsAdded === 1 ? "" : "s"} (${res.questionCount} total). ` +
          res.note,
        );
      }
      onDone();
    } catch (e) {
      setSubmitError(e instanceof ApiError ? e.message : "Injection failed.");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Manual quiz builder helpers ────────────────────────────────────────────
  function updateQuizQ(idx: number, patch: Partial<ManualQuizQ>) {
    setQuizQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  }
  function updateQuizOption(qIdx: number, oIdx: number, val: string) {
    setQuizQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIdx) return q;
        const opts = [...q.options];
        opts[oIdx] = val;
        return { ...q, options: opts };
      }),
    );
  }
  function addQuizQ() { setQuizQuestions((p) => [...p, emptyQuizQ()]); }
  function removeQuizQ(idx: number) { setQuizQuestions((p) => p.filter((_, i) => i !== idx)); }

  // ── Manual exam builder helpers ────────────────────────────────────────────
  function updateExamQ(idx: number, patch: Partial<ManualExamQ>) {
    setExamQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  }
  function updateExamOption(qIdx: number, oIdx: number, val: string) {
    setExamQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIdx) return q;
        const opts = [...q.options];
        opts[oIdx] = { ...opts[oIdx], text: val };
        return { ...q, options: opts };
      }),
    );
  }
  function addExamQ() { setExamQuestions((p) => [...p, emptyExamQ(p.length + 1)]); }
  function removeExamQ(idx: number) { setExamQuestions((p) => p.filter((_, i) => i !== idx)); }

  if (!open) return null;

  const examResources = existingResources.filter((r) => r.resourceType === "previous_exam");
  const n = countJsonItems();
  const btnLabel =
    mode === "quiz"
      ? `Inject Quiz Questions${inputMode === "json" && jsonText ? ` (${n})` : ""}`
      : `Inject Exam Questions${inputMode === "json" && jsonText ? ` (${n})` : ""}`;

  return (
    <>
      <Modal
        isOpen={true}
        onClose={handleClose}
        size="lg"
        showCloseButton={false}
        className="p-0 overflow-hidden max-w-2xl max-h-[92vh]"
      >
        <div className="w-full max-h-[92vh] overflow-y-auto admin-scroll-y flex flex-col">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <h2 className="font-bold text-lg">Inject Content Manually</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Bypass AI — paste curated questions directly into the system
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 bg-muted rounded-full flex items-center justify-center"
          >
            <X size={15} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 flex-1">
          {/* ── Mode switcher ── */}
          <div className="flex gap-2">
            {[
              { id: "unified", label: "📥 Unified Import (Content/Notes/Quiz)" },
              { id: "quiz", label: "🎯 Quiz (Legacy)" },
              { id: "exam", label: "📋 Past Exam Questions" },
            ].map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  setMode(m.id as ContentMode);
                  setJsonText("");
                  setJsonError("");
                  setSubmitError("");
                  setSubmitSuccess("");
                  setUnifiedResult(null);
                  setLatexRepairNotice(null);
                }}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition ${
                  mode === m.id
                    ? "bg-primary text-on-accent border-primary shadow-sm"
                    : "border-border hover:bg-muted text-muted-foreground"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* ── UNIFIED IMPORT VIEW ── */}
          {mode === "unified" && (
            <div className="space-y-4">
              <div className="text-xs rounded-xl px-4 py-3 bg-info/10 text-info border border-info/30 flex items-start gap-2.5">
                <Info size={16} className="shrink-0 mt-0.5 text-info" />
                <div>
                  <p className="font-semibold">Unified Module & Chapter Import</p>
                  <p className="mt-0.5 text-xs opacity-90">
                    Import <strong>Chapter Content</strong>, <strong>Short Notes</strong>, or <strong>Quiz Banks</strong>. 
                    Target course and module selection is required. Selected IDs override JSON fields. Supports single or multi-chapter JSON and multiple files at once.
                  </p>
                </div>
              </div>

              {/* ── Target Course & Module Selection (Required) ── */}
              <div className="bg-muted/40 border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                    Required Target Selection
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Overrides IDs in uploaded files
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* University (Optional) */}
                  <div>
                    <label className="block text-xs font-semibold mb-1 text-muted-foreground">
                      University (Optional)
                    </label>
                    <select
                      value={universityId}
                      onChange={(e) => setUniversityId(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-border text-xs bg-background text-foreground focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="">Common / All Universities</option>
                      {universities.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Course (Required) */}
                  <div>
                    <label className="block text-xs font-semibold mb-1 text-foreground">
                      Course <span className="text-destructive">*</span>
                    </label>
                    <select
                      value={courseId}
                      onChange={(e) => handleCourseChange(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-border text-xs bg-background text-foreground focus:ring-2 focus:ring-primary/30 font-medium"
                    >
                      <option value="">Select Course (Required)</option>
                      {courses.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.code} — {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Module (Required) */}
                  <div>
                    <label className="block text-xs font-semibold mb-1 text-foreground">
                      Module <span className="text-destructive">*</span>
                    </label>
                    <select
                      value={moduleId}
                      onChange={(e) => setModuleId(e.target.value)}
                      disabled={!courseId}
                      className="w-full px-3 py-2 rounded-lg border border-border text-xs bg-background text-foreground focus:ring-2 focus:ring-primary/30 font-medium disabled:opacity-50"
                    >
                      <option value="">
                        {!courseId
                          ? "Select a Course first"
                          : modules.length === 0
                          ? "No modules found for this course"
                          : "Select Module (Required)"}
                      </option>
                      {modules.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {(!courseId || !moduleId) && (
                  <div className="flex items-center gap-2 text-warning text-xs bg-warning/10 border border-warning/30 px-3 py-2 rounded-lg">
                    <AlertCircle size={14} className="shrink-0 text-warning" />
                    <span>Upload is blocked until both Course and Module are selected.</span>
                  </div>
                )}
              </div>

              {/* Template loaders & File upload */}
              <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs font-semibold text-muted-foreground mr-1">Templates:</span>
                  <button
                    type="button"
                    onClick={() => { setJsonText(CONTENT_TEMPLATE); setUnifiedResult(null); setLatexRepairNotice(null); }}
                    className="text-xs font-medium px-2.5 py-1 rounded-lg bg-success/10 hover:bg-success/10 text-success border border-success/30 transition"
                  >
                    📄 Content
                  </button>
                  <button
                    type="button"
                    onClick={() => { setJsonText(NOTES_TEMPLATE); setUnifiedResult(null); setLatexRepairNotice(null); }}
                    className="text-xs font-medium px-2.5 py-1 rounded-lg bg-info/10 hover:bg-info/10 text-info border border-info/30 transition"
                  >
                    📝 Notes
                  </button>
                  <button
                    type="button"
                    onClick={() => { setJsonText(UNIFIED_QUIZ_TEMPLATE); setUnifiedResult(null); setLatexRepairNotice(null); }}
                    className="text-xs font-medium px-2.5 py-1 rounded-lg bg-secondary hover:bg-secondary/80 text-secondary-foreground border border-border transition"
                  >
                    🎯 Quiz Bank
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <label
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg border flex items-center gap-1.5 transition ${
                      courseId && moduleId
                        ? "cursor-pointer bg-secondary hover:bg-secondary/80 text-foreground border-border"
                        : "cursor-not-allowed bg-muted text-muted-foreground border-border/50 opacity-60"
                    }`}
                  >
                    <FileUp size={13} /> Upload JSON (Multiple files supported)
                    <input
                      type="file"
                      multiple
                      accept=".json"
                      disabled={!courseId || !moduleId}
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                  {(selectedFiles.length > 0 || jsonText) && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFiles([]);
                        setJsonText("");
                        setUnifiedResult(null);
                        setLatexRepairNotice(null);
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground underline px-1"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>

              {/* Selected Files List */}
              {selectedFiles.length > 0 && (
                <div className="space-y-1.5 border border-border rounded-xl p-3 bg-card">
                  <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
                    <span>Selected Files ({selectedFiles.length})</span>
                    <span className="text-xs">Processed in sequential order</span>
                  </div>
                  <div className="space-y-1 max-h-40 overflow-y-auto admin-scroll-y">
                    {selectedFiles.map((f, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between text-xs px-2.5 py-1.5 bg-muted/40 rounded-lg border border-border/40"
                      >
                        <div className="flex items-center gap-2 truncate">
                          <FileText size={13} className="text-muted-foreground shrink-0" />
                          <span className="font-mono text-xs truncate">{f.name}</span>
                          <span className="text-xs text-muted-foreground">({Math.round(f.size / 1024)} KB)</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(idx)}
                          className="text-muted-foreground hover:text-destructive p-0.5"
                          title="Remove file"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detected Kind Badge */}
              <div className="flex items-center justify-between bg-muted/60 px-3 py-2 rounded-xl text-xs">
                <span className="text-muted-foreground font-medium">Detected Payload Kind:</span>
                {(() => {
                  const detected = detectPayloadKind(jsonText);
                  if (detected === "content") {
                    return <span className="font-bold text-success bg-success/10 px-2 py-0.5 rounded-md">Chapter Content</span>;
                  }
                  if (detected === "notes") {
                    return <span className="font-bold text-info bg-info/10 px-2 py-0.5 rounded-md">Short Notes</span>;
                  }
                  if (detected === "quiz") {
                    return <span className="font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-md">Quiz Question Bank</span>;
                  }
                  if (selectedFiles.length > 0) {
                    return <span className="font-bold text-primary">{selectedFiles.length} file(s) queued</span>;
                  }
                  return <span className="text-muted-foreground italic">None / Unknown</span>;
                })()}
              </div>

              {/* LaTeX Unescaped Warning & Repair Bar */}
              {checkUnescapedLatex(jsonText) && (
                <div className="bg-warning/10 border border-warning/30 rounded-xl p-3 flex items-center justify-between gap-3 text-xs">
                  <div className="flex items-center gap-2 text-warning">
                    <AlertCircle size={15} className="shrink-0 text-warning" />
                    <span>Unescaped LaTeX backslashes detected (e.g. \frac, \sum). JSON requires double backslashes.</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleRepairLatex}
                    disabled={repairingLatex}
                    className="px-3 py-1 bg-warning text-warning-foreground font-bold rounded-lg shrink-0 hover:bg-warning transition flex items-center gap-1"
                  >
                    {repairingLatex ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Repair LaTeX Now
                  </button>
                </div>
              )}

              {/* Repaired Notice */}
              {latexRepairNotice?.wasRepaired && (
                <div className="text-xs bg-success/10 border border-success/30 text-success rounded-xl p-3">
                  <p className="font-semibold">✓ Auto-repaired {latexRepairNotice.changeCount} unescaped LaTeX token(s) into valid JSON.</p>
                </div>
              )}

              {/* JSON Textarea (Only when no multiple files selected) */}
              {selectedFiles.length <= 1 && (
                <div>
                  <textarea
                    value={jsonText}
                    onChange={(e) => {
                      setJsonText(e.target.value);
                      setUnifiedResult(null);
                    }}
                    rows={8}
                    placeholder="Paste raw JSON content, notes, or quiz bank here…"
                    className="w-full font-mono text-xs px-3 py-2.5 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 leading-relaxed"
                  />
                </div>
              )}

              {/* Status & Options Controls */}
              <div className="bg-muted/40 border border-border rounded-xl p-3 flex items-center justify-between flex-wrap gap-3 text-xs">
                <div className="flex items-center gap-4">
                  <span className="font-semibold text-muted-foreground">Save Status:</span>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="unifiedStatus"
                      checked={unifiedStatus === "draft"}
                      onChange={() => setUnifiedStatus("draft")}
                      className="text-primary"
                    />
                    <span className="font-medium">Draft (Recommended)</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="unifiedStatus"
                      checked={unifiedStatus === "active"}
                      onChange={() => setUnifiedStatus("active")}
                      className="text-primary"
                    />
                    <span className="font-medium">Active</span>
                  </label>
                </div>

                <label className="flex items-center gap-2 cursor-pointer font-medium text-xs">
                  <input
                    type="checkbox"
                    checked={unifiedReplaceExisting}
                    onChange={(e) => setUnifiedReplaceExisting(e.target.checked)}
                    className="rounded border-border text-primary"
                  />
                  <span>Replace existing items (unchecked = append & skip dups)</span>
                </label>
              </div>

              {/* Action Buttons for Unified Import */}
              <div className="grid grid-cols-2 gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => handleUnifiedSubmit(true)}
                  disabled={unifiedDryRunning || unifiedSubmitting || !courseId || !moduleId || (!selectedFiles.length && !jsonText.trim())}
                  className="py-2.5 px-4 rounded-xl border border-primary text-primary font-bold text-xs hover:bg-primary/5 transition flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  {unifiedDryRunning ? <Loader2 size={13} className="animate-spin" /> : "🧪"} Run Dry Run (Preview)
                </button>
                <button
                  type="button"
                  onClick={() => handleUnifiedSubmit(false)}
                  disabled={unifiedDryRunning || unifiedSubmitting || !courseId || !moduleId || (!selectedFiles.length && !jsonText.trim())}
                  className="py-2.5 px-4 rounded-xl bg-primary text-primary-foreground font-bold text-xs hover:opacity-95 shadow-sm transition flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  {unifiedSubmitting ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />} Import to Database
                </button>
              </div>

              {/* Unified Import Results Display */}
              {unifiedResult && (
                <div className="mt-3 p-4 rounded-xl border border-border bg-card space-y-4 text-xs">
                  <div className="flex items-center justify-between border-b border-border pb-3">
                    <div>
                      <span className="font-bold text-sm">
                        {unifiedResult.dryRun ? "Dry Run Preview" : "Import Results"}
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Target Course: <strong>{courses.find((c) => c.id === courseId)?.name || courseId}</strong> | Module: <strong>{modules.find((m) => m.id === moduleId)?.title || moduleId}</strong>
                      </p>
                    </div>
                    <span className={`px-2.5 py-0.5 rounded-full font-bold ${
                      unifiedResult.valid
                        ? "bg-success/10 text-success border border-success/30"
                        : "bg-destructive/10 text-destructive border border-destructive/30"
                    }`}>
                      {unifiedResult.valid ? "✓ Valid Payload" : "✗ Validation Errors"}
                    </span>
                  </div>

                  {/* Multi-file results list */}
                  {Array.isArray(unifiedResult.results) ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Total Files Processed: {unifiedResult.results.length}</span>
                        <span>
                          {unifiedResult.results.filter((r: any) => r.valid).length} valid,{" "}
                          {unifiedResult.results.filter((r: any) => !r.valid).length} with errors
                        </span>
                      </div>

                      {unifiedResult.results.map((r: any, rIdx: number) => (
                        <div key={rIdx} className="border border-border/80 rounded-lg p-3 bg-muted/20 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-semibold text-foreground">{r.filename || `File #${rIdx + 1}`}</span>
                              <span className="uppercase text-xs px-1.5 py-0.5 rounded font-bold bg-secondary text-foreground">
                                {r.kind || "unknown"}
                              </span>
                            </div>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                              r.valid ? "text-success bg-success/10" : "text-destructive bg-destructive/10"
                            }`}>
                              {r.valid ? "Passed" : "Failed"}
                            </span>
                          </div>

                          {/* Chapters found */}
                          {r.chaptersFound !== undefined && (
                            <div className="text-xs text-muted-foreground">
                              Chapters Found: <strong>{r.chaptersFound}</strong>
                              {r.chapters && r.chapters.length > 0 && (
                                <span className="ml-1 opacity-80">
                                  ({r.chapters.map((ch: any) => `${ch.chapterTitle} [${ch.action}]`).join(", ")})
                                </span>
                              )}
                            </div>
                          )}

                          {/* Counts */}
                          {r.counts && (
                            <div className="grid grid-cols-4 gap-2 bg-background/80 p-2 rounded text-center text-xs">
                              <div><span className="text-muted-foreground block text-xs uppercase">Total</span><strong>{r.counts.total ?? 0}</strong></div>
                              <div><span className="text-muted-foreground block text-xs uppercase">New</span><strong className="text-success">{r.counts.new ?? 0}</strong></div>
                              <div><span className="text-muted-foreground block text-xs uppercase">Replaced</span><strong className="text-info">{r.counts.replaced ?? 0}</strong></div>
                              <div><span className="text-muted-foreground block text-xs uppercase">Skipped</span><strong className="text-warning">{r.counts.skipped ?? 0}</strong></div>
                            </div>
                          )}

                          {/* Warnings */}
                          {r.warnings && r.warnings.length > 0 && (
                            <div className="space-y-1 bg-warning/10 border border-warning/30 p-2 rounded text-warning text-xs">
                              <p className="font-bold flex items-center gap-1"><AlertCircle size={12} /> Warnings:</p>
                              {r.warnings.map((w: any, wIdx: number) => (
                                <p key={wIdx}>• {typeof w === "string" ? w : w.message}</p>
                              ))}
                            </div>
                          )}

                          {/* Errors */}
                          {r.errors && r.errors.length > 0 && (
                            <div className="space-y-1 bg-destructive/10 border border-destructive/30 p-2 rounded text-destructive text-xs">
                              <p className="font-bold flex items-center gap-1"><AlertCircle size={12} /> Errors ({r.errors.length}):</p>
                              {r.errors.map((e: any, eIdx: number) => (
                                <p key={eIdx}>• <strong className="font-mono">{e.path}:</strong> {e.message}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Single file results */
                    <div className="space-y-3">
                      {/* Chapters Found */}
                      {unifiedResult.chaptersFound !== undefined && (
                        <div className="text-xs text-muted-foreground">
                          Chapters Found: <strong>{unifiedResult.chaptersFound}</strong>
                          {unifiedResult.chapters && unifiedResult.chapters.length > 0 && (
                            <span className="ml-1 opacity-80">
                              ({unifiedResult.chapters.map((ch: any) => `${ch.chapterTitle} [${ch.action}]`).join(", ")})
                            </span>
                          )}
                        </div>
                      )}

                      {/* Summary Metrics */}
                      {unifiedResult.counts && (
                        <div className="grid grid-cols-4 gap-2 bg-muted/50 p-2.5 rounded-lg text-center">
                          <div>
                            <div className="text-xs text-muted-foreground uppercase tracking-wide">Total</div>
                            <div className="font-bold text-base mt-0.5">{unifiedResult.counts.total ?? 0}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground uppercase tracking-wide">New</div>
                            <div className="font-bold text-base mt-0.5 text-success">{unifiedResult.counts.new ?? 0}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground uppercase tracking-wide">Replaced</div>
                            <div className="font-bold text-base mt-0.5 text-info">{unifiedResult.counts.replaced ?? 0}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground uppercase tracking-wide">Skipped</div>
                            <div className="font-bold text-base mt-0.5 text-warning">{unifiedResult.counts.skipped ?? 0}</div>
                          </div>
                        </div>
                      )}

                      {/* Warnings */}
                      {unifiedResult.warnings && unifiedResult.warnings.length > 0 && (
                        <div className="space-y-1.5 bg-warning/10 border border-warning/30 p-2.5 rounded-lg text-warning text-xs">
                          <p className="font-bold flex items-center gap-1.5"><AlertCircle size={14} /> Warnings:</p>
                          {unifiedResult.warnings.map((w: any, idx: number) => (
                            <p key={idx}>• {typeof w === "string" ? w : w.message}</p>
                          ))}
                        </div>
                      )}

                      {/* Duplicates Report */}
                      {unifiedResult.duplicates && unifiedResult.duplicates.length > 0 && (
                        <div className="space-y-1.5">
                          <p className="font-semibold text-warning flex items-center gap-1">
                            <AlertCircle size={13} /> {unifiedResult.duplicates.length} Duplicate Question(s) Skipped:
                          </p>
                          <div className="space-y-1 border border-border rounded-lg p-2 bg-muted/30">
                            {unifiedResult.duplicates.map((d: any, idx: number) => (
                              <div key={idx} className="p-1.5 bg-background rounded text-xs border border-border/50">
                                <span className="font-semibold text-warning">[{d.reason}]</span>{" "}
                                <span className="truncate inline-block max-w-[280px] align-bottom">{d.question}</span>
                                {d.similarity && <span className="ml-2 opacity-75 font-mono">({Math.round(d.similarity * 100)}% match)</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Errors Report */}
                      {unifiedResult.errors && unifiedResult.errors.length > 0 && (
                        <div className="space-y-1.5">
                          <p className="font-semibold text-destructive flex items-center gap-1">
                            <AlertCircle size={13} /> {unifiedResult.errors.length} Validation Error(s):
                          </p>
                          <div className="space-y-1 border border-destructive/30  rounded-lg p-2 bg-destructive/10">
                            {unifiedResult.errors.map((e: any, idx: number) => (
                              <div key={idx} className="text-xs text-destructive">
                                <span className="font-mono font-bold">{e.path}:</span> {e.message}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── LEGACY QUIZ & EXAM CONTROLS (Only shown when not in unified mode) ── */}
          {mode !== "unified" && (
            <>
              <div
                className={`text-xs rounded-lg px-3 py-2.5 ${mode === "quiz"
                  ? "bg-info/10 text-info border border-info/30"
                  : "bg-warning/10 text-warning border border-warning/30"
                  }`}
              >
                {mode === "quiz"
                  ? "Quiz questions go into the AI quiz cache — students receive them through the normal Get Quiz flow. Select the course and optionally the module it belongs to."
                  : "Past exam questions will create or update a published past exam resource. Enter the exam title and select the course. No pre-uploaded resource is required!"}
              </div>

              {/* ── Course ── */}
              <div>
                <label className="block text-xs font-semibold mb-1">
                  Course <span className="text-destructive">*</span>
                </label>
                <select
                  value={courseId}
                  onChange={(e) => handleCourseChange(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-background text-foreground"
                >
                  <option value="">Select a course</option>
                  {courses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} — {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* ── Exam Title & Metadata (exam mode only) ── */}
              {mode === "exam" && (
                <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold mb-1">
                  Exam Title / Name <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  value={examTitle}
                  onChange={(e) => setExamTitle(e.target.value)}
                  placeholder="e.g. Communicative English Language Skills I Final Exam 2014 E.C."
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1">Exam Class</label>
                  <select
                    value={examClass}
                    onChange={(e) =>
                      setExamClass(e.target.value as "" | "final" | "mid" | "coc")
                    }
                    className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-background text-foreground"
                  >
                    <option value="">None</option>
                    <option value="final">Final Exam</option>
                    <option value="mid">Midterm Exam</option>
                    <option value="coc">CoC Exam</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">Year</label>
                  <input
                    type="text"
                    value={examYear}
                    onChange={(e) => setExamYear(e.target.value)}
                    placeholder="e.g. 2014"
                    className="w-full px-3 py-2 rounded-lg border border-border text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Module (quiz only) ── */}
          {mode === "quiz" && courseId && (
            <div>
              <label className="block text-xs font-semibold mb-1">
                Module{" "}
                <span className="text-muted-foreground font-normal">
                  (optional — scopes the quiz cache)
                </span>
              </label>
              {modulesLoading ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                  <Loader2 size={12} className="animate-spin" /> Loading modules…
                </div>
              ) : modules.length === 0 ? (
                <div className="text-xs text-warning bg-warning/10 border border-warning/30 rounded-lg px-3 py-2">
                  No uploaded modules for this course. The quiz will be served course-wide.
                  To scope by module, upload the module PDF first via Upload Resource.
                </div>
              ) : (
                <select
                  value={moduleId}
                  onChange={(e) => setModuleId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-background text-foreground"
                >
                  <option value="">No specific module (course-wide)</option>
                  {modules.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.title}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* ── Quiz meta fields ── */}
          {mode === "quiz" && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold mb-1">
                  Subject <span className="text-destructive">*</span>
                </label>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. Physics"
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Difficulty</label>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value as "Easy" | "Medium" | "Hard")}
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-background text-foreground"
                >
                  <option>Easy</option>
                  <option>Medium</option>
                  <option>Hard</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Exam class</label>
                <select
                  value={examClass}
                  onChange={(e) =>
                    setExamClass(e.target.value as "" | "final" | "mid" | "coc")
                  }
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm bg-background text-foreground"
                >
                  <option value="">None</option>
                  <option value="final">Final</option>
                  <option value="mid">Midterm</option>
                  <option value="coc">CoC</option>
                </select>
              </div>
            </div>
          )}

          {/* ── Status selector (quiz only) ── */}
          {mode === "quiz" && (
            <div className="flex items-center gap-3 bg-muted/20 p-2.5 rounded-lg border border-border">
              <label className="text-xs font-semibold">Publish Status:</label>
              <select
                value={quizStatus}
                onChange={(e) => setQuizStatus(e.target.value as "active" | "draft")}
                className="px-2.5 py-1 rounded-md border border-border text-xs bg-background text-foreground"
              >
                <option value="active">Active (Published immediately)</option>
                <option value="draft">Draft (Review in queue before publishing)</option>
              </select>
              <span className="text-xs text-muted-foreground">
                {quizStatus === "active"
                  ? "Questions will be available to students right away."
                  : "Questions will be placed into the Review Queue for approval."}
              </span>
            </div>
          )}

          {/* ── Replace toggle ── */}
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={replaceExisting}
              onChange={(e) => setReplaceExisting(e.target.checked)}
              className="w-4 h-4 rounded accent-primary"
            />
            <span className="text-sm font-medium">
              {mode === "quiz" ? "Replace existing cache entry" : "Replace all existing questions"}
            </span>
            <span className="text-xs text-muted-foreground">
              {mode === "quiz"
                ? "(off = skip if entry already exists)"
                : "(off = append, skip duplicate numbers)"}
            </span>
          </label>

          {/* ── Input mode switcher ── */}
          <div className="flex gap-2 border border-border rounded-lg p-1 bg-muted/30">
            {(["json", "manual"] as const).map((im) => (
              <button
                key={im}
                type="button"
                onClick={() => { setInputMode(im); setJsonError(""); }}
                className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition ${inputMode === im ? "bg-primary text-on-accent shadow" : "hover:bg-muted"
                  }`}
              >
                {im === "json" ? "Paste JSON" : "Build manually"}
              </button>
            ))}
          </div>

          {/* ── JSON input ── */}
          {inputMode === "json" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold">
                  Questions JSON
                  <span className="ml-2 text-muted-foreground font-normal">
                    — same format as what the AI generates
                  </span>
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  success={templateLoaded}
                  successText="Template Loaded!"
                  onClick={loadTemplate}
                  className="text-xs text-primary font-semibold hover:underline shrink-0 p-0 h-auto bg-transparent hover:bg-transparent"
                >
                  Load template
                </Button>
              </div>
              <textarea
                id="admin-inject-json"
                value={jsonText}
                onChange={(e) => {
                  setJsonText(e.target.value);
                  setJsonError("");
                  setUnifiedResult(null);
                  setSubmitSuccess("");
                  setSubmitError("");
                }}
                placeholder={
                  mode === "quiz"
                    ? `[\n  {\n    "question": "...",\n    "options": ["A","B","C","D"],\n    "correct": 0,\n    "difficulty": "Medium",\n    "explanation": "..."\n  }\n]`
                    : `[\n  {\n    "number": 1,\n    "text": "...",\n    "options": [{\"label\":\"A\",\"text\":\"...\"}],\n    "answer": {\"label\":\"A\",\"explanation\":\"...\"}\n  }\n]`
                }
                className="w-full h-64 px-3 py-2.5 rounded-lg border border-border text-xs font-mono resize-y bg-muted/30 focus:outline-none focus:ring-2 focus:ring-primary/30"
                spellCheck={false}
              />
              {jsonError && (
                <p className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2">
                  {jsonError}
                </p>
              )}
            </div>
          )}

          {/* ── Manual quiz builder ── */}
          {inputMode === "manual" && mode === "quiz" && (
            <div className="space-y-4">
              {quizQuestions.map((q, qi) => (
                <div key={qi} className="border border-border rounded-xl p-4 space-y-3 bg-muted/20">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                      Question {qi + 1}
                    </p>
                    {quizQuestions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeQuizQ(qi)}
                        className="text-destructive hover:text-destructive"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <textarea
                    value={q.question}
                    onChange={(e) => updateQuizQ(qi, { question: e.target.value })}
                    placeholder="Question text (markdown / LaTeX ok)"
                    className="w-full px-3 py-2 rounded-lg border border-border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                    rows={2}
                  />
                  <p className="text-xs text-muted-foreground">Options (click radio = correct answer)</p>
                  <div className="grid grid-cols-2 gap-2">
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex gap-2 items-center">
                        <input
                          type="radio"
                          name={`correct-${qi}`}
                          checked={q.correct === oi}
                          onChange={() => updateQuizQ(qi, { correct: oi })}
                          className="shrink-0 accent-primary"
                        />
                        <input
                          value={opt}
                          onChange={(e) => updateQuizOption(qi, oi, e.target.value)}
                          placeholder={`Option ${oi + 1}`}
                          className="flex-1 px-2 py-1.5 rounded-md border border-border text-sm"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Difficulty</label>
                      <select
                        value={q.difficulty}
                        onChange={(e) =>
                          updateQuizQ(qi, { difficulty: e.target.value as "Easy" | "Medium" | "Hard" })
                        }
                        className="w-full px-2 py-1.5 rounded-md border border-border text-sm bg-background text-foreground"
                      >
                        <option>Easy</option>
                        <option>Medium</option>
                        <option>Hard</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Chapter title</label>
                      <input
                        value={q.chapterTitle}
                        onChange={(e) => updateQuizQ(qi, { chapterTitle: e.target.value })}
                        placeholder="Optional"
                        className="w-full px-2 py-1.5 rounded-md border border-border text-sm"
                      />
                    </div>
                  </div>
                  <input
                    value={q.explanation}
                    onChange={(e) => updateQuizQ(qi, { explanation: e.target.value })}
                    placeholder="Explanation (optional)"
                    className="w-full px-2 py-1.5 rounded-md border border-border text-sm"
                  />
                  <div className="pt-2">
                    <label className="text-xs font-semibold text-muted-foreground block mb-1">
                      Question Figure (Optional / Reference Image)
                    </label>
                    <QuestionFigureUploader
                      token={token || ""}
                      figureStatus={q.figure_status}
                      figureImage={q.figure_image}
                      figureAlt={q.figure_alt}
                      figureLocation={q.figure_location}
                      onChange={(fig) => updateQuizQ(qi, {
                        figure_status: fig.figure_status,
                        figure_image: fig.figure_image,
                        figure_alt: fig.figure_alt,
                        figure_location: fig.figure_location,
                      })}
                    />
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addQuizQ}
                className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
              >
                <Plus size={14} /> Add question
              </button>
            </div>
          )}

          {/* ── Manual exam builder ── */}
          {inputMode === "manual" && mode === "exam" && (
            <div className="space-y-4">
              {examQuestions.map((q, qi) => (
                <div key={qi} className="border border-border rounded-xl p-4 space-y-3 bg-muted/20">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                      Question {q.number}
                    </p>
                    {examQuestions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeExamQ(qi)}
                        className="text-destructive hover:text-destructive"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <textarea
                    value={q.text}
                    onChange={(e) => updateExamQ(qi, { text: e.target.value })}
                    placeholder="Question text"
                    className="w-full px-3 py-2 rounded-lg border border-border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                    rows={2}
                  />
                  <p className="text-xs text-muted-foreground">Options (click radio = correct answer)</p>
                  <div className="space-y-1.5">
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex gap-2 items-center">
                        <input
                          type="radio"
                          name={`exam-correct-${qi}`}
                          checked={q.answer.label === opt.label}
                          onChange={() =>
                            updateExamQ(qi, { answer: { ...q.answer, label: opt.label } })
                          }
                          className="shrink-0 accent-primary"
                        />
                        <span className="text-xs font-bold w-5 text-muted-foreground shrink-0">
                          {opt.label}
                        </span>
                        <input
                          value={opt.text}
                          onChange={(e) => updateExamOption(qi, oi, e.target.value)}
                          placeholder={`Option ${opt.label}`}
                          className="flex-1 px-2 py-1.5 rounded-md border border-border text-sm"
                        />
                      </div>
                    ))}
                  </div>
                  <input
                    value={q.answer.explanation}
                    onChange={(e) =>
                      updateExamQ(qi, { answer: { ...q.answer, explanation: e.target.value } })
                    }
                    placeholder="Explanation (optional)"
                    className="w-full px-2 py-1.5 rounded-md border border-border text-sm"
                  />
                  <div className="pt-2">
                    <label className="text-xs font-semibold text-muted-foreground block mb-1">
                      Question Figure (Optional / Reference Image)
                    </label>
                    <QuestionFigureUploader
                      token={token || ""}
                      figureStatus={q.figure_status}
                      figureImage={q.figure_image}
                      figureAlt={q.figure_alt}
                      figureLocation={q.figure_location}
                      onChange={(fig) => updateExamQ(qi, {
                        figure_status: fig.figure_status,
                        figure_image: fig.figure_image,
                        figure_alt: fig.figure_alt,
                        figure_location: fig.figure_location,
                      })}
                    />
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addExamQ}
                className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
              >
                <Plus size={14} /> Add question
              </button>
              </div>
            )}
          </>
        )}

          {/* ── Feedback (Legacy) ── */}
          {mode !== "unified" && submitSuccess && (
            <div className="text-sm text-success bg-success/10 border border-success/30 rounded-lg px-4 py-3">
              {submitSuccess}
            </div>
          )}
          {mode !== "unified" && submitError && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3">
              {submitError}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        {mode !== "unified" ? (
          <div className="px-6 pb-6 pt-4 border-t border-border sticky bottom-0 bg-card">
            <Button
              id="admin-inject-submit"
              type="button"
              onClick={handleSubmit}
              loading={submitting}
              loadingText={mode === "quiz" ? "Injecting Quiz…" : "Injecting Questions…"}
              success={!!submitSuccess}
              successText={mode === "quiz" ? "Quiz Injected!" : "Questions Injected!"}
              error={!!submitError}
              errorText="Injection Failed"
              variant="primary"
              size="lg"
              icon={<Check size={16} />}
              className="w-full py-3"
            >
              {btnLabel}
            </Button>
          </div>
        ) : (
          <div className="px-6 py-3 border-t border-border bg-card flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Close
            </button>
          </div>
        )}
        </div>
      </Modal>

      <ShortNotesAdminPreviewModal
        open={showShortNotesPreview}
        onClose={() => setShowShortNotesPreview(false)}
        token={token}
        importResult={unifiedResult}
        onApproved={() => {
          setShowShortNotesPreview(false);
          onDone();
        }}
      />
    </>
  );
}
